import type { ImportPlan } from '../../app/backupImport';
import { SheetB } from '../SheetB';
import { summariseImportB } from './moreB';
import type { DeviceLocalB, ImportSideB, ProfileNowB } from './moreB';

/**
 * B-19 — **the import plan preview** (design spec §2.4's More row; scenario 35).
 *
 * `app/backupImport` states the contract this sheet exists to honour: *"There is deliberately no
 * one-shot `importArchive(bytes)`. BAK-4 requires a confirm stating the counts of what will be
 * removed."* `planImport` has already parsed, validated and counted, and has **destroyed
 * nothing**; this sheet is the step between that and `applyImport`, and the confirm button is the
 * only thing in B that can reach the second call.
 *
 * Replace-all is stated in words, not implied by a count. A user reading "3 matches will be
 * imported" could reasonably believe they are gaining three matches; the heading says everything
 * on the device is replaced, and the removing column says by how much.
 */

function Side({
  side,
  testId,
  empty,
}: {
  readonly side: ImportSideB;
  readonly testId: string;
  readonly empty: string;
}) {
  if (side.lines.length === 0) return <p className="more-plan__empty">{empty}</p>;
  return (
    <ul className="more-plan__list" data-testid={testId}>
      {side.lines.map((line) => (
        <li key={line.key} className="more-plan__row">
          <span className="more-plan__label">{line.label}</span>
          <span className="more-plan__count">{line.count}</span>
        </li>
      ))}
    </ul>
  );
}

export interface ImportPlanSheetProps {
  readonly plan: ImportPlan;
  readonly filename: string;
  /** What this device holds that no archive can carry — see {@link DeviceLocalB}. */
  readonly deviceLocal: DeviceLocalB;
  /** What this device's profile holds that THIS file may not put back — see {@link ProfileNowB}. */
  readonly profileNow: ProfileNowB;
  readonly busy: boolean;
  readonly problem: string | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function ImportPlanSheet({
  plan,
  filename,
  deviceLocal,
  profileNow,
  busy,
  problem,
  onConfirm,
  onCancel,
}: ImportPlanSheetProps) {
  const summary = summariseImportB(plan, deviceLocal, profileNow);

  return (
    <SheetB
      titleId="more-plan-h"
      title="Import this archive?"
      testId="more-import-plan"
      onClose={onCancel}
      actions={
        <>
          {/* Cancel is FIRST in the DOM deliberately: `showModal()` focuses the first focusable
              element, and this sheet's other button is a destructive replace-all — which would
              otherwise open pre-focused, one Enter away. (React's `autoFocus` prop cannot fix
              this: it focuses imperatively at commit, before `showModal()` re-runs its own focus
              logic, and it never sets the `autofocus` attribute that `showModal()` reads.) */}
          <button
            type="button"
            className="more-btn"
            data-testid="more-import-cancel"
            disabled={busy}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="more-btn more-btn--danger"
            data-testid="more-import-confirm"
            disabled={busy}
            onClick={onConfirm}
          >
            Replace everything
          </button>
        </>
      }
    >
      <p className="more-plan__file" data-testid="more-plan-file">
        {filename}
      </p>
      {summary.formatVersions.length > 0 && (
        <p className="more-plan__version">archive format {summary.formatVersions.join(', ')}</p>
      )}

      <p className="more-plan__lede">
        Importing <strong>replaces everything on this device</strong> with what is in this file.
        There is no merge, and no undo — export first if you want to keep what is here.
      </p>

      {summary.warnings.length > 0 && (
        <ul className="more-plan__warnings" data-testid="more-plan-warnings">
          {summary.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}

      <h3 className="more-plan__heading">
        Removed from this device ({summary.removing.total})
      </h3>
      <Side side={summary.removing} testId="more-plan-removing" empty="Nothing — this device is empty." />

      <h3 className="more-plan__heading">Imported from the file ({summary.importing.total})</h3>
      <Side
        side={summary.importing}
        testId="more-plan-importing"
        empty="Nothing — this file carries no rows."
      />

      {problem !== null && (
        <p className="more-problem" data-testid="more-import-problem" role="status">
          {problem}
        </p>
      )}
    </SheetB>
  );
}
